import { type ReactNode, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { btnInk, btnShu, ChapterHead, Check, Stamp } from "../app/components/Cram";
import { CHUNK_FONT } from "../app/lib/rsvp";
import { detectLang, t } from "../i18n";

export function Landing() {
  const i = t(detectLang(useLocation().pathname));
  const [line1, line2] = i.heroTitle.split("\n");
  const chapters = [
    { id: "problem", kicker: i.problemKicker, title: i.problemTitle },
    { id: "model", kicker: i.modelKicker, title: i.modelTitle },
    { id: "why", kicker: i.whyKicker, title: i.whyTitle },
    { id: "pricing", kicker: i.pricingKicker, title: i.pricingTitle },
  ];
  const modes = [
    { name: i.layer1, audio: i.layer1Audio, desc: i.layer1Desc },
    { name: i.layer2, audio: i.layer2Audio, desc: i.layer2Desc },
    { name: i.layer3, audio: i.layer3Audio, desc: i.layer3Desc },
    { name: i.layer4, audio: i.layer4Audio, desc: i.layer4Desc },
  ];

  return (
    <main className="mx-auto max-w-5xl px-4 sm:px-6">
      <section className="mt-6 border-[3px] border-ink bg-sheet sm:mt-10">
        <div className="flex items-center justify-between gap-3 bg-shu px-4 py-1.5 text-white">
          <span className="text-[12px] font-black tracking-[0.2em] sm:text-[13px]">{i.coverBand}</span>
          <span className="font-mono text-[11px] font-bold tracking-widest">RSVP</span>
        </div>
        <div className="grid md:grid-cols-[1.35fr_1fr]">
          <div className="px-5 py-7 sm:px-8 sm:py-10">
            <div className="flex flex-wrap gap-2">
              {i.coverStamps.map((s, k) => (
                <Stamp key={s} tone={k === 2 ? "solid" : "shu"} tilt={k === 1}>
                  {s}
                </Stamp>
              ))}
            </div>
            <h1 className="mt-6 text-[44px] leading-[1.08] font-black tracking-tight sm:text-[68px]">
              <span className="block">{line1}</span>
              <span className="block text-shu">{line2}</span>
            </h1>
            <p className="mt-6 max-w-lg font-mincho text-[15px] leading-8 text-ink-2">{i.heroDesc}</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <a href="/auth/login" className={btnShu}>
                {i.ctaConnect}
              </a>
              <a href="#toc" className={btnInk}>
                {i.ctaDocs}
              </a>
            </div>
          </div>
          <nav
            id="toc"
            className="scroll-mt-20 border-t-2 border-ink px-5 py-6 sm:px-8 md:border-t-0 md:border-l-2 md:py-10"
          >
            <div className="flex items-baseline justify-between border-b-2 border-ink pb-1.5">
              <span className="text-[20px] font-black tracking-[0.3em]">{i.tocTitle}</span>
              <span className="font-mono text-[10px] font-bold tracking-widest text-ink-3">CONTENTS</span>
            </div>
            <ol className="mt-2">
              {chapters.map((c, k) => (
                <li key={c.id} className="border-b border-rule">
                  <a href={`#${c.id}`} className="group block py-2.5">
                    <span className="flex items-baseline gap-2 text-[11px] font-bold text-shu">
                      {i.chapter(k + 1)}
                      <span className="text-ink-3">{c.kicker}</span>
                    </span>
                    <span className="mt-0.5 flex items-baseline gap-2">
                      <span className="text-[14px] leading-snug font-bold group-hover:text-shu">{c.title}</span>
                      <span className="leader" />
                      <span className="font-mono text-[12px] font-bold">§{k + 1}</span>
                    </span>
                  </a>
                </li>
              ))}
            </ol>
          </nav>
        </div>
      </section>

      <section className="mt-10 border-2 border-ink bg-sheet">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b-2 border-ink px-4 py-2.5">
          <span className="bg-ink px-2 py-0.5 text-[12px] font-black tracking-widest text-paper">{i.exampleLabel}</span>
          <span className="font-mincho text-[14px] font-bold">{i.exampleTask}</span>
        </div>
        <Demo chunks={i.demoChunks} />
        <p className="border-t border-dashed border-ink-3 px-4 py-2.5 text-[12px] text-ink-2">
          <span className="mr-2 font-black text-shu">【{i.answerLabel}】</span>
          {i.demoCaption}
        </p>
      </section>

      <Chapter no={i.chapter(1)} {...chapters[0]}>
        <p className="ruled max-w-3xl font-mincho text-[16px]">{i.problemDesc}</p>
      </Chapter>

      <Chapter no={i.chapter(2)} {...chapters[1]}>
        <div className="border-2 border-ink bg-sheet">
          <div className="hatch hidden grid-cols-[3rem_14rem_11rem_1fr] border-b-2 border-ink text-[12px] font-black md:grid">
            <span className="px-3 py-2">No.</span>
            {i.modeCols.map((c) => (
              <span key={c} className="border-l border-ink px-3 py-2">
                {c}
              </span>
            ))}
          </div>
          {modes.map((m, k) => (
            <div
              key={m.name}
              className="grid grid-cols-[2.5rem_1fr] border-b border-ink last:border-b-0 md:grid-cols-[3rem_14rem_11rem_1fr]"
            >
              <span className="row-span-3 grid place-items-center border-r border-ink font-mono text-[18px] font-bold text-shu md:row-span-1">
                {String(k + 1).padStart(2, "0")}
              </span>
              <span className="px-3 pt-3 text-[15px] font-black md:py-3">{m.name}</span>
              <span className="px-3 pt-1 text-[12px] font-bold text-ink-2 md:border-l md:border-ink md:py-3">
                <span className="mr-1 text-ink-3 md:hidden">{i.modeCols[1]}:</span>
                {m.audio}
              </span>
              <span className="px-3 pt-1 pb-3 font-mincho text-[14px] leading-7 md:border-l md:border-ink md:py-3">
                {m.desc}
              </span>
            </div>
          ))}
        </div>
        <Note label={i.noteLabel}>{i.modelNote}</Note>
      </Chapter>

      <Chapter no={i.chapter(3)} {...chapters[2]}>
        <div className="grid gap-4 md:grid-cols-2">
          {[
            [i.why1Title, i.why1Desc],
            [i.why2Title, i.why2Desc],
          ].map(([title, desc], k) => (
            <div key={title} className="border-2 border-ink bg-sheet">
              <span className="inline-block bg-shu px-2 py-0.5 text-[11px] font-black tracking-widest text-white">
                {i.pointLabel} {k + 1}
              </span>
              <div className="px-4 pt-2 pb-4">
                <h3 className="text-[17px] font-black">
                  <span className="marker">{title}</span>
                </h3>
                <p className="mt-2 font-mincho text-[14px] leading-7 text-ink-2">{desc}</p>
              </div>
            </div>
          ))}
        </div>
        <Note label="※">{i.whyResearch}</Note>
      </Chapter>

      <Chapter no={i.chapter(4)} {...chapters[3]}>
        <div className="border-2 border-ink bg-sheet">
          <div className="flex items-center justify-between bg-ink px-4 py-2 text-paper">
            <span className="text-[14px] font-black tracking-[0.3em]">{i.priceListTitle}</span>
            <span className="font-mono text-[10px] tracking-widest">PRICE LIST</span>
          </div>
          <div className="hatch hidden grid-cols-[2.5rem_9rem_1fr_10rem] border-b-2 border-ink text-[12px] font-black sm:grid">
            <span />
            {i.priceCols.map((c, k) => (
              <span key={c} className={`border-l border-ink px-3 py-1.5 ${k === 2 ? "text-right" : ""}`}>
                {c}
              </span>
            ))}
          </div>
          {[
            { name: i.freePlanName, desc: i.freePlanDesc, price: i.freePrice, pro: false },
            { name: i.proPlanName, desc: i.proPlanDesc, price: i.proPrice, pro: true },
          ].map((p) => (
            <div
              key={p.name}
              className={`grid grid-cols-[2.5rem_1fr_auto] border-b border-ink sm:grid-cols-[2.5rem_9rem_1fr_10rem] ${p.pro ? "bg-shu-pale" : ""}`}
            >
              <span className="row-span-2 grid place-items-center border-r border-ink sm:row-span-1">
                <Check on={p.pro} />
              </span>
              <span className="flex flex-wrap items-center gap-2 px-3 pt-3 text-[16px] font-black sm:py-3">
                {p.name}
                {p.pro && <Stamp>{i.proStamp}</Stamp>}
              </span>
              <span className="row-span-2 flex flex-col items-end justify-center px-3 py-3 sm:order-last sm:row-span-1 sm:border-l sm:border-ink">
                <span className={`font-mono text-[26px] leading-none font-bold ${p.pro ? "text-shu" : ""}`}>
                  {p.price}
                </span>
                {p.pro && <span className="mt-1 text-[10px] font-bold text-ink-2">{i.priceUnit}</span>}
              </span>
              <span className="px-3 pt-1 pb-3 font-mincho text-[13px] leading-6 sm:border-l sm:border-ink sm:py-3">
                {p.desc}
              </span>
            </div>
          ))}
          <dl className="grid grid-cols-[4.5rem_1fr] text-[12px] sm:grid-cols-[6rem_1fr] [&>div:last-child>*]:border-b-0">
            {i.pricingTerms.map(([k, v]) => (
              <div key={k} className="contents">
                <dt className="hatch border-r border-b border-ink px-3 py-2 font-black">{k}</dt>
                <dd className="border-b border-ink px-3 py-2 leading-relaxed">{v}</dd>
              </div>
            ))}
          </dl>
        </div>
        <div className="mt-5">
          <a href="/auth/login" className={btnShu}>
            {i.ctaConnect}
          </a>
        </div>
      </Chapter>

      <section className="mx-auto mt-16 max-w-xl border-[3px] border-ink bg-sheet">
        <div className="flex items-center justify-between bg-shu px-4 py-1.5 text-white">
          <span className="text-[13px] font-black tracking-[0.3em]">{i.quotaTitle}</span>
          <span className="font-mono text-[11px] font-bold">05:00</span>
        </div>
        <div className="px-5 py-6 sm:px-8">
          <h2 className="text-[28px] leading-tight font-black sm:text-[34px]">{i.ctaFooterTitle}</h2>
          <ul className="mt-4 flex flex-col gap-2">
            {i.quotaItems.map((q) => (
              <li key={q} className="flex items-center gap-3 border-b border-rule pb-2 text-[14px] font-bold">
                <Check on={false} />
                {q}
              </li>
            ))}
          </ul>
          <p className="mt-4 font-mincho text-[14px] text-ink-2">{i.ctaFooterDesc}</p>
          <a href="/auth/login" className={`${btnShu} mt-5 w-full`}>
            {i.ctaConnect}
          </a>
        </div>
      </section>
    </main>
  );
}

function Chapter({
  no,
  id,
  kicker,
  title,
  children,
}: {
  no: string;
  id: string;
  kicker: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="mt-14">
      <ChapterHead id={id} no={no} kicker={kicker} title={<span className="marker">{title}</span>} />
      <div className="mt-5">{children}</div>
    </section>
  );
}

function Note({ label, children }: { label: string; children: ReactNode }) {
  return (
    <p className="mt-4 flex gap-2 text-[12px] leading-relaxed text-ink-2">
      <span className="shrink-0 font-black text-shu">{label.length > 1 ? `【${label}】` : label}</span>
      <span>{children}</span>
    </p>
  );
}

function Demo({ chunks }: { chunks: string[] }) {
  const [k, setK] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setK((n) => (n + 1) % (chunks.length + 1)), 750);
    return () => clearInterval(id);
  }, [chunks.length]);
  return (
    <div className="flex flex-col items-center px-4 py-6" aria-hidden>
      <div className="h-5 w-[3px] bg-shu" />
      <div className="my-1 w-full max-w-xl border-y border-rule">
        <div
          className="flex h-24 items-center justify-center text-center text-[30px] font-medium sm:text-[40px]"
          style={{ fontFamily: CHUNK_FONT }}
        >
          {chunks[k] ?? ""}
        </div>
      </div>
      <div className="h-5 w-[3px] bg-shu" />
    </div>
  );
}
